import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, effect, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { LanguageService } from '../services/language.service';
import { CurrencyResolverService } from '../services/currency-resolver.service';
import {
  MarketplaceFilterOption,
  MarketplaceSignals,
  PublicLoad,
  PublicLoadFilterOptions,
  PublicLoadSearchParams,
  PublicPage,
  PublicTrustSummary
} from './public-marketplace.models';
import { PublicMarketplaceService } from './public-marketplace.service';

@Component({
  selector: 'app-public-loads-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    SelectModule
  ],
  templateUrl: './public-loads-list.component.html',
  styleUrls: ['./public-marketplace.shared.css']
})
export class PublicLoadsListComponent implements OnInit {
  private static readonly FILTER_STORAGE_KEY = 'loadgate:public-loads:filters:v1';
  private readonly fb = inject(FormBuilder);
  private readonly currencyResolver = inject(CurrencyResolverService);
  private readonly service = inject(PublicMarketplaceService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly languageService = inject(LanguageService);

  readonly pageSize = 12;
  filtersOpen = false;

  form = this.fb.group({
    origin: [''],
    destination: [''],
    equipmentType: [''],
    paymentTerms: [''],
    weightKg: [''],
    pallets: [''],
    commodity: ['']
  });

  filterOptions: PublicLoadFilterOptions = {
    origins: [],
    destinations: [],
    equipmentTypes: [],
    paymentTerms: [],
    commodities: [],
    currencyCodes: [],
    originOptions: [],
    destinationOptions: [],
    equipmentTypeOptions: [],
    paymentTermOptions: [],
    commodityOptions: []
  };
  page: PublicPage<PublicLoad> | null = null;
  loads: PublicLoad[] = [];
  loading = false;
  error = '';
  private initialized = false;
  private suppressFilterPersistence = false;
  private restoredPageNumber = 0;
  private pendingScrollRestore: number | null = null;

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      if (!this.initialized) {
        return;
      }
      this.loadFilterOptions();
      this.loadLoads(this.page?.number ?? 0);
    });
  }

  get originOptions(): Array<{ label: string; value: string }> {
    return this.preferredOptions(this.filterOptions.originOptions, this.filterOptions.origins);
  }

  get destinationOptions(): Array<{ label: string; value: string }> {
    return this.preferredOptions(this.filterOptions.destinationOptions, this.filterOptions.destinations);
  }

  get equipmentOptions(): Array<{ label: string; value: string }> {
    return this.preferredOptions(this.filterOptions.equipmentTypeOptions, this.filterOptions.equipmentTypes);
  }

  get paymentTermsOptions(): Array<{ label: string; value: string }> {
    return this.preferredOptions(this.filterOptions.paymentTermOptions, this.filterOptions.paymentTerms);
  }

  get commodityOptions(): Array<{ label: string; value: string }> {
    return this.preferredOptions(this.filterOptions.commodityOptions, this.filterOptions.commodities);
  }

  ngOnInit(): void {
    this.restoreFilterState();
    this.initialized = true;
    this.loadFilterOptions();
    this.loadLoads(this.restoredPageNumber);
  }

  applyFilters(): void {
    this.pendingScrollRestore = 0;
    this.loadLoads(0);
  }

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  clearFilters(): void {
    this.clearStoredFilterState();
    this.suppressFilterPersistence = true;
    this.form.reset({
      origin: '',
      destination: '',
      equipmentType: '',
      paymentTerms: '',
      weightKg: '',
      pallets: '',
      commodity: ''
    });
    this.pendingScrollRestore = 0;
    this.loadLoads(0);
    this.suppressFilterPersistence = false;
  }

  previousPage(): void {
    if (!this.page || this.page.number === 0) {
      return;
    }
    this.pendingScrollRestore = 0;
    this.loadLoads(this.page.number - 1);
  }

  nextPage(): void {
    if (!this.page || this.page.number + 1 >= this.page.totalPages) {
      return;
    }
    this.pendingScrollRestore = 0;
    this.loadLoads(this.page.number + 1);
  }

  login(): void {
    this.router.navigate(['/login'], {
      queryParams: { returnUrl: this.router.url || '/' }
    });
  }

  loadLoads(page: number): void {
    this.restoredPageNumber = page;
    this.persistFilterState();
    this.loading = true;
    this.error = '';
    this.service.getLoads(this.buildParams(page)).subscribe({
      next: result => {
        this.page = result;
        this.loads = result.content ?? [];
        this.loading = false;
        this.restoreScrollPositionOnce();
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = this.text('Unable to load public loads right now.', 'تعذر تحميل الشحنات العامة حالياً.');
        this.loads = [];
        this.page = null;
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  trackById(_: number, load: PublicLoad): number {
    return load.id;
  }

  openLoad(loadId: number): void {
    this.persistFilterState();
    this.router.navigate(['/public/loads', loadId]);
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return this.text('Flexible', 'مرن');
    }
    return new Intl.DateTimeFormat(this.locale(), {
      timeZone: 'Asia/Riyadh',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(this.parseDate(value) ?? new Date(value));
  }

  formatRelativeTime(value?: string | null): string {
    const date = this.parseDate(value);
    if (!date) {
      return this.text('Recently', 'حديثًا');
    }

    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) {
      return this.text('just now', 'الآن');
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return this.languageService.currentLanguage() === 'ar'
        ? minutes === 1
          ? 'منذ دقيقة'
          : `منذ ${minutes} دقائق`
        : `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return this.languageService.currentLanguage() === 'ar'
        ? hours === 1
          ? 'منذ ساعة'
          : `منذ ${hours} ساعات`
        : `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
      return this.languageService.currentLanguage() === 'ar'
        ? days === 1
          ? 'منذ يوم'
          : `منذ ${days} أيام`
        : `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return this.languageService.currentLanguage() === 'ar'
        ? months === 1
          ? 'منذ شهر'
          : `منذ ${months} أشهر`
        : `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }

    const years = Math.floor(months / 12);
    return this.languageService.currentLanguage() === 'ar'
      ? years === 1
        ? 'منذ سنة'
        : `منذ ${years} سنوات`
      : `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }

  formatMoney(value?: number | null, currency?: string | null): string {
    if (value === null || value === undefined) {
      return this.text('Rate on request', 'السعر عند الطلب');
    }
    return this.currencyResolver.formatCurrency(value, {
      currencyCode: currency,
      locale: this.locale(),
      maximumFractionDigits: 0
    });
  }

  signalBadges(load: PublicLoad): Array<{ icon: string; label: string; strong?: boolean }> {
    const signals = load.signals;
    const badges: Array<{ icon: string; label: string; strong?: boolean }> = [];
    this.addDemandBadges(badges, signals);
    if (signals?.verifiedShipper) {
      badges.push({ icon: 'pi pi-verified', label: this.text('Verified shipper', 'مرسل موثّق'), strong: true });
    }
    return badges;
  }

  joinList(values?: string[] | null): string {
    return values?.filter(Boolean).join(', ') || this.text('Any', 'أي');
  }

  hasTagValue(value?: string | null): boolean {
    const normalized = this.normalizeTagValue(value);
    return normalized.length > 0;
  }

  tagValue(value?: string | null): string {
    return this.normalizeTagValue(value);
  }

  trustTitle(trust?: PublicTrustSummary | null): string {
    if (!this.isTrustAvailable(trust)) {
      return this.text('Trust profile not available', 'ملف الثقة غير متاح');
    }
    return trust?.trustBadge || this.translateTrustValue(trust?.verificationStatus) || this.text('Trust reviewed', 'تمت مراجعة ملف الثقة');
  }

  trustSubtitle(trust?: PublicTrustSummary | null): string {
    const availableTrust = this.availableTrust(trust);
    if (!availableTrust) {
      return this.text('Not available', 'غير متاح');
    }
    return (
      availableTrust.publicRecommendationNote ||
      this.trustSignalText(availableTrust) ||
      this.text('Redacted trust signals available', 'إشارات الثقة المنقحة متاحة')
    );
  }

  labelize(value?: string | null): string {
    return value ? value.replace(/_/g, ' ').toLowerCase() : '';
  }

  isTrustAvailable(trust?: PublicTrustSummary | null): boolean {
    return this.availableTrust(trust) !== null;
  }

  private availableTrust(trust?: PublicTrustSummary | null): PublicTrustSummary | null {
    if (!trust) {
      return null;
    }
    const status = (trust.verificationStatus || '').trim().toLowerCase();
    if (status === 'unverified') {
      return null;
    }
    return trust;
  }

  trustScoreLabel(trust?: PublicTrustSummary | null): string {
    if (!this.isTrustAvailable(trust) || trust?.trustScore === null || trust?.trustScore === undefined) {
      return '';
    }
    return `${trust.trustScore}/100`;
  }

  text(en: string, ar: string): string {
    return this.languageService.currentLanguage() === 'ar' ? ar : en;
  }

  private addDemandBadges(
    badges: Array<{ icon: string; label: string; strong?: boolean }>,
    signals?: MarketplaceSignals | null
  ): void {
    if (!signals) {
      return;
    }
    if ((signals.viewCount || 0) > 0) {
      badges.push({
        icon: 'pi pi-eye',
        label: this.languageService.currentLanguage() === 'ar'
          ? `تمت المشاهدة ${signals.viewCount} مرة`
          : `Viewed ${signals.viewCount} times`
      });
    }
    if ((signals.offersSubmittedCount || 0) > 0) {
      badges.push({
        icon: 'pi pi-send',
        label: this.languageService.currentLanguage() === 'ar'
          ? `${signals.offersSubmittedCount} عروض`
          : `${signals.offersSubmittedCount} offers`
      });
    }
    if ((signals.interestedUsersCount || 0) > 0) {
      badges.push({
        icon: 'pi pi-users',
        label: this.languageService.currentLanguage() === 'ar'
          ? `${signals.interestedUsersCount} مهتم`
          : `${signals.interestedUsersCount} interested`
      });
    }
    if (signals.hotLane) {
      badges.push({ icon: 'pi pi-bolt', label: this.text('Hot lane', 'مسار ساخن'), strong: true });
    }
    if (signals.fastPickup) {
      badges.push({ icon: 'pi pi-stopwatch', label: this.text('Fast pickup', 'استلام سريع'), strong: true });
    }
  }

  private translateTrustValue(value?: string | null): string {
    const key = (value || '').trim().toUpperCase();
    const map: Record<string, string> = {
      VERIFIED: this.text('Verified', 'موثّق'),
      UNVERIFIED: this.text('Unverified', 'غير موثّق'),
      LIMITED_HISTORY: this.text('Limited history', 'سجل محدود'),
      COMMUNICATION_ONLY: this.text('Communication only', 'التواصل فقط'),
      MARKETPLACE_LEAD: this.text('Marketplace lead', 'ملف جديد'),
      TRUSTED: this.text('Trusted', 'موثوق'),
      EMERGING: this.text('Emerging', 'في البداية'),
      ESTABLISHING: this.text('Establishing', 'في طور البناء')
    };
    return map[key] || this.labelize(value) || '';
  }

  onLocationOptionFilter(type: 'origin' | 'destination' | 'equipment' | 'paymentTerms' | 'commodity', event: unknown): void {
    const term = this.extractFilterTerm(event);
    if (!term) {
      this.setLazyOptions(type, []);
      return;
    }

    const params = this.lazyOptionParams(type, term);

    this.service.getLoadFilterOptions({ ...params, lang: this.languageService.currentLanguage() }).subscribe({
      next: options => {
        const values =
          type === 'origin'
            ? options.origins
            : type === 'destination'
              ? options.destinations
              : type === 'equipment'
                ? options.equipmentTypes
                : type === 'paymentTerms'
                  ? options.paymentTerms
                  : options.commodities;
        const displayOptions =
          type === 'origin'
            ? options.originOptions
            : type === 'destination'
              ? options.destinationOptions
              : type === 'equipment'
                ? options.equipmentTypeOptions
                : type === 'paymentTerms'
                  ? options.paymentTermOptions
                  : options.commodityOptions;
        this.setLazyOptions(type, values ?? [], displayOptions ?? this.toOptions(values));
        this.cdr.detectChanges();
      },
      error: () => {
        this.setLazyOptions(type, []);
        this.cdr.detectChanges();
      }
    });
  }

  private buildParams(page: number): PublicLoadSearchParams {
    const value = this.form.getRawValue();
    return {
      page,
      size: this.pageSize,
      origin: value.origin,
      destination: value.destination,
      equipmentType: value.equipmentType,
      paymentTerms: value.paymentTerms,
      weightKg: value.weightKg,
      pallets: value.pallets,
      commodity: value.commodity,
      lang: this.languageService.currentLanguage()
    };
  }

  private toOptions(values?: string[] | null): MarketplaceFilterOption[] {
    return (values ?? []).map(value => ({ label: value, value }));
  }

  private preferredOptions(
    options?: MarketplaceFilterOption[] | null,
    values?: string[] | null
  ): Array<{ label: string; value: string }> {
    return options?.length ? options : this.toOptions(values);
  }

  private parseDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(this.normalizeDateValue(value));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private normalizeDateValue(value: string): string {
    return /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`;
  }

  private normalizeTagValue(value?: string | null): string {
    const normalized = (value || '').trim();
    if (!normalized) {
      return '';
    }
    const upper = normalized.toUpperCase();
    if (upper === 'N/A' || upper === 'NA' || upper === 'NOT AVAILABLE' || upper === 'NOT LISTED') {
      return '';
    }
    return normalized;
  }

  private trustSignalText(trust: PublicTrustSummary): string {
    const signals = [
      this.labelize(trust.verificationStatus),
      this.labelize(trust.paymentConfidence),
      this.labelize(trust.assuranceStatus)
    ].filter(Boolean);
    return signals.join(' | ');
  }

  private extractFilterTerm(event: unknown): string {
    const filter = (event as { filter?: unknown })?.filter;
    return typeof filter === 'string' ? filter.trim() : '';
  }

  private lazyOptionParams(
    type: 'origin' | 'destination' | 'equipment' | 'paymentTerms' | 'commodity',
    term: string
  ): { origin?: string; destination?: string; equipmentType?: string; paymentTerms?: string; commodity?: string } {
    if (type === 'origin') return { origin: term };
    if (type === 'destination') return { destination: term };
    if (type === 'equipment') return { equipmentType: term };
    if (type === 'paymentTerms') return { paymentTerms: term };
    return { commodity: term };
  }

  private setLazyOptions(
    type: 'origin' | 'destination' | 'equipment' | 'paymentTerms' | 'commodity',
    values: string[],
    options: MarketplaceFilterOption[] = this.toOptions(values)
  ): void {
    this.filterOptions = {
      ...this.filterOptions,
      origins: type === 'origin' ? values : this.filterOptions.origins,
      destinations: type === 'destination' ? values : this.filterOptions.destinations,
      equipmentTypes: type === 'equipment' ? values : this.filterOptions.equipmentTypes,
      paymentTerms: type === 'paymentTerms' ? values : this.filterOptions.paymentTerms,
      commodities: type === 'commodity' ? values : this.filterOptions.commodities,
      originOptions: type === 'origin' ? options : this.filterOptions.originOptions,
      destinationOptions: type === 'destination' ? options : this.filterOptions.destinationOptions,
      equipmentTypeOptions: type === 'equipment' ? options : this.filterOptions.equipmentTypeOptions,
      paymentTermOptions: type === 'paymentTerms' ? options : this.filterOptions.paymentTermOptions,
      commodityOptions: type === 'commodity' ? options : this.filterOptions.commodityOptions
    };
  }

  private locale(): string {
    return this.languageService.currentLanguage() === 'ar' ? 'ar-SA' : 'en';
  }

  private loadFilterOptions(): void {
    this.service.getLoadFilterOptions({ lang: this.languageService.currentLanguage() }).subscribe({
      next: options => (this.filterOptions = this.mergeSelectedFilterOptions(options)),
      error: () => {
        this.filterOptions = this.mergeSelectedFilterOptions({
          origins: [],
          destinations: [],
          equipmentTypes: [],
          paymentTerms: [],
          commodities: [],
          currencyCodes: [],
          originOptions: [],
          destinationOptions: [],
          equipmentTypeOptions: [],
          paymentTermOptions: [],
          commodityOptions: []
        });
      }
    });
  }

  private persistFilterState(): void {
    if (this.suppressFilterPersistence || !this.hasBrowserStorage()) {
      return;
    }

      const value = this.form.getRawValue();
      const hasValue = Object.values(value).some(item => `${item ?? ''}`.trim().length > 0);
      try {
      if (hasValue || this.restoredPageNumber > 0 || (this.pendingScrollRestore ?? this.currentScrollY()) > 0) {
        localStorage.setItem(PublicLoadsListComponent.FILTER_STORAGE_KEY, JSON.stringify({
          filters: value,
          pageNumber: this.restoredPageNumber,
          scrollY: this.pendingScrollRestore ?? this.currentScrollY()
        }));
      } else {
        localStorage.removeItem(PublicLoadsListComponent.FILTER_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures; filters should still work without persistence.
    }
  }

  private restoreFilterState(): void {
    if (!this.hasBrowserStorage()) {
      return;
    }

    try {
      const raw = localStorage.getItem(PublicLoadsListComponent.FILTER_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const saved = JSON.parse(raw) as {
        filters?: Record<string, string | number | null>;
        pageNumber?: number;
        scrollY?: number;
      } & Record<string, string | number | null | undefined>;
      const filters = saved.filters ?? saved;
      this.form.patchValue({
        origin: this.restoreString(filters['origin']),
        destination: this.restoreString(filters['destination']),
        equipmentType: this.restoreString(filters['equipmentType']),
        paymentTerms: this.restoreString(filters['paymentTerms']),
        weightKg: this.restoreString(filters['weightKg']),
        pallets: this.restoreString(filters['pallets']),
        commodity: this.restoreString(filters['commodity'])
      });
      this.restoredPageNumber = Number.isFinite(saved.pageNumber) ? Number(saved.pageNumber) : 0;
      this.pendingScrollRestore = Number.isFinite(saved.scrollY) ? Number(saved.scrollY) : null;
      this.filterOptions = this.mergeSelectedFilterOptions(this.filterOptions);
    } catch {
      this.clearStoredFilterState();
    }
  }

  private mergeSelectedFilterOptions(options: PublicLoadFilterOptions): PublicLoadFilterOptions {
    const value = this.form.getRawValue();
    return {
      ...options,
      origins: this.includeSelectedValue(options.origins, value.origin),
      destinations: this.includeSelectedValue(options.destinations, value.destination),
      equipmentTypes: this.includeSelectedValue(options.equipmentTypes, value.equipmentType),
      paymentTerms: this.includeSelectedValue(options.paymentTerms, value.paymentTerms),
      commodities: this.includeSelectedValue(options.commodities, value.commodity),
      originOptions: this.includeSelectedOption(options.originOptions, value.origin),
      destinationOptions: this.includeSelectedOption(options.destinationOptions, value.destination),
      equipmentTypeOptions: this.includeSelectedOption(options.equipmentTypeOptions, value.equipmentType),
      paymentTermOptions: this.includeSelectedOption(options.paymentTermOptions, value.paymentTerms),
      commodityOptions: this.includeSelectedOption(options.commodityOptions, value.commodity)
    };
  }

  private includeSelectedValue(values: string[] = [], selected?: string | null): string[] {
    const value = (selected || '').trim();
    if (!value || values.includes(value)) {
      return values;
    }
    return [value, ...values];
  }

  private includeSelectedOption(
    options: MarketplaceFilterOption[] = [],
    selected?: string | null
  ): MarketplaceFilterOption[] {
    const value = (selected || '').trim();
    if (!value || options.some(option => option.value === value)) {
      return options;
    }
    return [{ label: value, value }, ...options];
  }

  private restoreString(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? `${value}` : '';
  }

  private clearStoredFilterState(): void {
    if (!this.hasBrowserStorage()) {
      return;
    }
    try {
      localStorage.removeItem(PublicLoadsListComponent.FILTER_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }

  private hasBrowserStorage(): boolean {
    return typeof localStorage !== 'undefined';
  }

  private restoreScrollPositionOnce(): void {
    const scrollY = this.pendingScrollRestore;
    if (scrollY === null || typeof window === 'undefined') {
      return;
    }

    setTimeout(() => {
      window.scrollTo({ top: scrollY, behavior: 'auto' });
      this.pendingScrollRestore = null;
    });
  }

  private currentScrollY(): number {
    return typeof window === 'undefined' ? 0 : window.scrollY || 0;
  }
}
